import { useCallback, useEffect, useRef, useState } from "react";
export interface UseImagePreloadOptions {
  lazy?: boolean;
  preloadDistance?: string;
  fadeThreshold?: number;
}

export type ImageLoadStatus = "idle" | "loading" | "loaded" | "error";

export interface ImagePreloadState {
  status: ImageLoadStatus;
  loadDuration?: number;
  shouldFade?: boolean;
}

export const useImagePreload = (
  url: string,
  options: UseImagePreloadOptions = {}
): ImagePreloadState & {
  setRef: (element: HTMLElement | null) => void;
} => {
  const {
    lazy = false,
    preloadDistance = "50px",
    fadeThreshold = 100,
  } = options;

  const [state, setState] = useState<ImagePreloadState>(() => {
    return {
      status: "idle",
      loadDuration: undefined,
      shouldFade: false,
    };
  });

  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementRef = useRef<HTMLElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const loadImage = useCallback(() => {
    setState((prev) => {
      // Only start loading if not already loaded or loading
      if (prev.status === "loaded" || prev.status === "loading") {
        return prev;
      }

      // Clean up any existing image to prevent race conditions
      if (imageRef.current) {
        imageRef.current.onload = null;
        imageRef.current.onerror = null;
      }

      // Start loading and track timing
      const startTime = performance.now();
      const img = new Image();
      imageRef.current = img;

      img.onload = () => {
        const loadDuration = performance.now() - startTime;

        setState({
          status: "loaded",
          loadDuration,
          shouldFade: loadDuration > fadeThreshold,
        });
      };

      img.onerror = () => {
        const loadDuration = performance.now() - startTime;
        setState({
          status: "error",
          loadDuration,
          shouldFade: false,
        });
      };

      img.src = url;

      return { ...prev, status: "loading" as const };
    });
  }, [url, fadeThreshold]);

  const setRef = useCallback(
    (element: HTMLElement | null) => {
      elementRef.current = element;

      if (!lazy || !element) {
        if (!lazy) {
          loadImage();
        }
        return;
      }

      // Check if IntersectionObserver is supported
      if (typeof IntersectionObserver === "undefined") {
        // Fallback: load immediately if no IntersectionObserver support
        loadImage();
        return;
      }

      // Clean up existing observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Create new observer
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting) {
            loadImage();
            // Stop observing after first intersection
            observerRef.current?.disconnect();
          }
        },
        { rootMargin: preloadDistance }
      );

      observerRef.current.observe(element);
    },
    [lazy, preloadDistance, loadImage]
  );

  // Cleanup on unmount or URL change
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (imageRef.current) {
        imageRef.current.onload = null;
        imageRef.current.onerror = null;
      }
    };
  }, [url]);

  // Reset state when URL changes
  useEffect(() => {
    setState({
      status: "idle",
    });

    // Disconnect observer when URL changes
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // If not lazy, start loading immediately
    if (!lazy) {
      loadImage();
    } else if (elementRef.current) {
      // Re-setup observer for new URL
      setRef(elementRef.current);
    }
  }, [url, lazy, loadImage, setRef]);

  return { ...state, setRef };
};
